import React from 'react';
import { useTranslation } from 'react-i18next';
//import { useNavigate } from 'react-router-dom';
import { Skeleton } from "@heroui/react";

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { copyProjectInfo, copyProjectLimits, copyProjectRoom, HotelInfo, HotelLimits, HotelRoom } from '@/features/projects/projectsSlice';
import { FieldDescriptor, getField, setField, UrlInfo } from '@/features/channels/channelsSlice';
import {
  AppGroup,
  AppPlatform,
  appPlatformCaption,
  groupApps,
  isAppAvailable,
  setAppAvailable,
} from '@/features/apps/appGroups';

// The platform columns of the admin table, in the order asked for in #25.
const availablePlatformColumns: AppPlatform[] = ['webos', 'philips', 'tizen', 'android'];

import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import { WelField } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input'

import { CatHeader } from './Project';
import { WelcomeField } from './WelcomeWizard';

export interface EnabledAppsProps {
  project:				HotelInfo;
  setProject:			(val: HotelInfo) => any;
  limits:				HotelLimits | null;
  setLimits:			(lim: HotelLimits) => any;
  room?:				HotelRoom;
  setRoom?:				(room: HotelRoom) => any;
}

const EnabledApps = (props: EnabledAppsProps) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);
  const apps = useAppSelector((state) => state.apps);
  const logins = useAppSelector((state) => state.logins);
  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username || !props.project) return false;
    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === props.project.id) return true;

    if (props.project.owner === username) return true;
    if (!props.limits) return false;
    const execs = props.limits.executives;
    return execs?.partner?.includes(username) ||
           execs?.chain_manager?.includes(username) ||
           execs?.manager?.includes(username);
  }, [logins.login, props.project, props.limits]);
  const seeAllOnly = role?.privilages?.includes("SeeAllProjects") && !role.admin && !role?.privilages?.includes("EditProjectSettings") && !isProjectMember;

  // Admins choose which apps the project's managers get to see; everyone
  // else is held to that choice. The same split as the Manager ACLs tab.
  const canMakeAvailable = role.admin || !!role?.privilages?.includes("SetManagerACLs");

  React.useEffect(() => {
    store.getApps();
  }, []);

  const groups = React.useMemo(() => groupApps(apps.apps), [apps.apps]);

  const acls = props.limits?.acls || [];

  // Managers only see the apps an admin made available (off by default).
  const listedApps = React.useMemo(() => {
    return	canMakeAvailable
    		?	groups
    		:	groups.filter((group: AppGroup) => isAppAvailable(acls, group.name));
  }, [groups, canMakeAvailable, acls]);

  const updateAvailable = React.useCallback((name: string, available: boolean) => {
    if (!props.limits)
      return;

    let limits = copyProjectLimits(props.limits);
    limits.acls = setAppAvailable(limits.acls, name, available);
    props.setLimits(limits);
  }, [props.limits, props.setLimits]);

  const updateRoomApps = React.useCallback((fields: UrlInfo[]) => {
	if (!props.room)
	  return;
    
	let room = copyProjectRoom(props.room);
	room.apps.apps = fields;

	if (props.setRoom)
	  props.setRoom(room);
  }, [props.room, props.setRoom]);
  
  const updateLimitsApps = React.useCallback((fields: UrlInfo[]) => {
    if (!props.limits)
	  return;
    
    let limits = copyProjectLimits(props.limits);
      
    limits.apps.apps = fields;
    props.setLimits(limits);
  }, [props.limits, props.setLimits]);
  
  if (props.project.is_new)
    return null;
  
  const caption = 	props.room
					?	"apps:room:caption"
					:	"apps:enable_apps";
  
  const loading =	!props.project.is_new &&
			  		props.project.id != '' &&
			  		!props.limits;

  const limitsApps	=	props.limits
  						?	props.limits.apps.apps
  						:	[];

  // Project level, for admins: one table with what each app runs on, whether
  // managers get it, and whether it is on for the project.
  if (canMakeAvailable && !props.room)
    return (
    <FlexRow direction="column"
             align="start"
             className="py-0 gap-[16px]">
      <CatHeader caption={caption} />
      <div className="r12 text-[--Dark-Grey]">
        { t('apps:available:hint') }
      </div>
      <table className="r14 text-left border-collapse">
        <thead>
          <tr className="border-b border-gray-200 font-medium">
            <th className="py-[8px] pr-[32px]">{ t('apps:app:name:caption') }</th>
            {availablePlatformColumns.map((p: AppPlatform) => (
              <th key={p} className="py-[8px] pr-[32px]">{ t(appPlatformCaption[p]) }</th>
            ))}
            <th className="py-[8px] pr-[32px]">{ t('apps:available:caption') }</th>
            <th className="py-[8px]">{ t('apps:enabled') }</th>
          </tr>
        </thead>
        <tbody>
        {groups.map((app: AppGroup) => {
          const value = getField(limitsApps, app.name);
          const enabled = value != '' && value != '__disabled__';

          return (
          <tr key={app.name} className="border-b border-gray-200">
            <td className="py-[4px] pr-[32px]">{ app.name }</td>
            {availablePlatformColumns.map((p: AppPlatform) => (
              <td key={p} className="py-[4px] pr-[32px]">
                { app.platforms[p]?.id ? t('apps:app:yes') : t('apps:app:no') }
              </td>
            ))}
            <td className="py-[4px] pr-[32px]">
              <CheckBox toggled={isAppAvailable(acls, app.name)}
                        isLoaded={!loading}
                        isDisabled={seeAllOnly}
                        setToggled={(val: boolean) => updateAvailable(app.name, val)} />
            </td>
            <td className="py-[4px]">
              <CheckBox toggled={enabled}
                        isLoaded={!loading}
                        isDisabled={seeAllOnly}
                        setToggled={(val: boolean) => updateLimitsApps(setField(limitsApps, app.name, val ? "1" : ""))} />
            </td>
          </tr>
          );
        })}
        </tbody>
      </table>
    </FlexRow>
    );

  return (
  <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
    <CatHeader caption={caption} />
    {listedApps.map((app: AppGroup, i: number) => {
      const field: FieldDescriptor = {
      	field:			app.name,
      	label:			app.name,
      	placeholder:	"",
      	desc:			`Enable app '${app.name}'`,
      	type:			"checkbox",
      	defval:			"1",
      	visibility:		["hotel", "room"],
      	acl:			false,
      	options:		[],
      };
      
      return (
      <WelcomeField key={i}
		            type=""
		            caption={field.label}
		            fields={	props.room
            					?	props.room.apps.apps
            					:	limitsApps
		            	   }
		            setFields={	props.room
	            					?	updateRoomApps
	            					:	updateLimitsApps
		            		  }
		            projFields={limitsApps}
		            limits={props.limits}
		            room={props.room}
		            isLoaded={!loading}
		            acl={false}
		            isReadOnly={seeAllOnly}
		            field={field} />
      );
    })}
  </FlexRow>
  );
}

/*export interface AppFieldProps extends EnabledAppsProps {
  app:					AppDescriptor;
  isLoaded?:			boolean;
}

export const AppField = (props: AppFieldProps) => {
  const { t } = useTranslation();
  const loaded = props.isLoaded === undefined
  				 ?	true
  				 :	props.isLoaded;
  
  const val =	props.room
  				?	props.room.apps.names.indexOf(props.app.name) > -1
  				:	(	props.limits
		  				?	props.limits.apps.names.indexOf(props.app.name) > -1
		  				:	false
		  			);
  
  const updateValue = React.useCallback((enabled: boolean) => {
    if (!props.limits)
      return;
    
    let changes = false;
    let limits = copyProjectLimits(props.limits);
    const index = limits.apps.names.indexOf(props.app.name);
    
    if (enabled) {
      if (index < 0) {
        limits.apps.names.push(props.app.name);
        changes = true;
      }
    } else
    if (index > -1) {
      limits.apps.names.splice(index, 1);
      changes = true;
    }
    
    if (changes)
      props.setLimits(limits);
  }, [props.limits, props.app]);
  
  const updateValueRoom = React.useCallback((enabled: boolean) => {
    if (	!props.room ||
        	!props.setRoom
       )
      return;
    
    let changes = false;
    let room = copyProjectRoom(props.room);
    const index = room.apps.names.indexOf(props.app.name);
    
    if (enabled) {
      if (index < 0) {
        room.apps.names.push(props.app.name);
        changes = true;
      }
    } else
    if (index > -1) {
      room.apps.names.splice(index, 1);
      changes = true;
    }
    
    if (changes)
      props.setRoom(room);
  }, [props.limits, props.room, props.app]);
  
  return (
    <WelField key={0} 
              label={props.app.name}>
      <CheckBox toggled={val}
                isLoaded={loaded}
                setToggled={props.room ? updateValueRoom : updateValue} />
    </WelField>
  );
};*/

export default EnabledApps;




